import { agentHooks } from '../hooks';
import { Button } from '../../../components/ui/Button';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { AgentStatusActions } from './AgentStatusActions';
import { AgentForm } from './AgentForm';
import { AgentTerritoryTab } from './AgentTerritoryTab';
import { AgentPerformanceTab } from './AgentPerformanceTab';
import { ResourceAuditTrail } from '../../audit/components/ResourceAuditTrail';
import { defineDetailPage, useDetailPage } from '@repo/detail';
import { DetailPage } from '@repo/web-detail';
import type { AgentDetail } from '../types';

interface AgentProfileProps {
  agentId: string;
}

const detailConfig = defineDetailPage<AgentDetail>({
  resource: 'agents',
  fetchById: async () => { throw new Error('fetchById not used directly'); },
  tabs: [
    { id: 'profile', label: 'Profile' },
    { id: 'territory', label: 'Territory' },
    { id: 'performance', label: 'Performance' },
    { id: 'collections', label: 'Collection History' },
    { id: 'audit', label: 'Audit' },
  ],
});

export function AgentProfile({ agentId }: AgentProfileProps) {
  const { data: response, isLoading, error, refetch } = agentHooks.useGet(agentId);
  const detail = useDetailPage(detailConfig, agentId, { enabled: false });
  const agent = (response && typeof response === 'object' && 'data' in response && response.data
    ? (response as { data: AgentDetail }).data
    : response) as AgentDetail | undefined;

  const renderTabContent = () => {
    if (!agent) return null;
    switch (detail.activeTab) {
      case 'profile':
        return <AgentForm initialValues={agent} agentId={agent.id} />;
      case 'territory':
        return <AgentTerritoryTab agentId={agent.id} />;
      case 'performance':
        return <AgentPerformanceTab agentId={agent.id} />;
      case 'collections':
        return (
          <div className="py-12 text-center text-text-muted border border-border rounded-md bg-surface-1">
            Collections module not yet implemented.
          </div>
        );
      case 'audit':
        return <ResourceAuditTrail resourceType="agent" resourceId={agent.id} />;
      default:
        return null;
    }
  };

  return (
    <DetailPage
      config={detailConfig}
      detail={{ ...detail, data: agent, isLoading, isError: !!error || !agent, error: error as Error | null, refetch }}
      title={agent?.fullName}
      subtitle={agent ? (agent.mobile + (agent.email ? ` • ${agent.email}` : '')) : undefined}
      headerActions={
        agent ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => detail.setActiveTab('profile')}>
              Edit Agent
            </Button>
            <AgentStatusActions agent={agent} />
          </div>
        ) : undefined
      }
      mainPane={
        agent ? (
          <div className="pt-2">
            {renderTabContent()}
          </div>
        ) : null
      }
      asidePane={
        agent ? (
          <div className="flex flex-col gap-4">
            <div className="bg-surface-1 border border-border rounded-lg p-6 flex flex-col gap-4">
              <h3 className="font-semibold text-text-primary">Agent Details</h3>
              <div className="flex flex-col gap-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Agent ID</span>
                  <span className="font-mono text-text-primary">{agent.agentNumber}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Status</span>
                  <StatusBadge 
                    status={agent.status === 'ACTIVE' ? 'success' : agent.status === 'SUSPENDED' ? 'warning' : 'danger'} 
                    label={agent.status} 
                  />
                </div>
              </div>
            </div>
          </div>
        ) : undefined
      }
    />
  );
}
